import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MetricCard, Loader, EmptyState, StatusBadge, Money } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Truck, Wallet, PiggyBank, ClipboardList, ArrowRight, Sparkles } from "lucide-react";

export default function Home() {
  const { profile } = useAuth();
  const nav = useNavigate();
  const [dash, setDash] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/retailer/dashboard").then((r) => setDash(r.data)).catch(() => setDash({}));
    api.get("/marketplace/products?sort=highest_margin&limit=4").then((r) => setProducts(r.data.products)).catch(() => {});
  }, []);

  if (!dash) return <Loader label="Loading home…" />;

  return (
    <div className="space-y-6">
      {profile && profile.verification_status !== "VERIFIED" && (
        <div data-testid="retailer-pending-banner" className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
          <b>Account pending verification.</b> You can browse the marketplace freely — you'll be able to place orders once VIPER OWL admin verifies your shop.
        </div>
      )}
      <div className="rounded-xl bg-forest p-6 text-white">
        <p className="overline text-psage">Welcome back</p>
        <h1 className="mt-1 font-heading text-2xl font-semibold">{profile?.shop_name || "Your Shop"}</h1>
        <p className="mt-1 text-sm text-psage">Discover verified manufacturers and buy at trade prices.</p>
        <Button data-testid="home-browse-btn" className="mt-4 bg-white text-forest hover:bg-psage" onClick={() => nav("/retailer/marketplace")}>
          Browse marketplace <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard testid="home-orders" label="Total Orders" value={<span className="num">{dash.total_orders || 0}</span>} icon={ClipboardList} accent />
        <MetricCard testid="home-transit" label="In Transit" value={<span className="num">{dash.in_transit || 0}</span>} icon={Truck} />
        <MetricCard testid="home-pending" label="Pending Payments" value={<span className="num">{dash.pending_payments || 0}</span>} icon={Wallet} />
        <MetricCard testid="home-savings" label="Trade Savings" value={<Money value={dash.trade_savings || 0} />} icon={PiggyBank} accent />
      </div>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-xl font-semibold text-forest flex items-center gap-2"><Sparkles className="h-5 w-5 text-olive" /> Highest margin picks</h2>
          <Link to="/retailer/marketplace" className="text-sm text-olive hover:underline">See all</Link>
        </div>
        {products.length === 0 ? (
          <EmptyState title="No products yet" hint="Products from verified manufacturers will appear here." />
        ) : (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {products.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 font-heading text-xl font-semibold text-forest">Recent orders</h2>
        {(!dash.recent_orders || dash.recent_orders.length === 0) ? (
          <EmptyState title="No orders yet." hint="Your orders will show up here once you make a purchase." icon={ClipboardList} />
        ) : (
          <div className="space-y-2">
            {dash.recent_orders.map((o) => (
              <Link key={o.id} to={`/retailer/orders/${o.id}`} data-testid={`home-order-${o.id}`}
                className="flex items-center justify-between rounded-xl border border-border bg-card p-4 transition-colors hover:bg-psage/30">
                <div>
                  <p className="num text-sm font-medium text-forest">{o.order_number}</p>
                  <p className="text-xs text-ink/50">{o.manufacturer_name}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Money value={o.total} className="text-forest" />
                  <StatusBadge status={o.status} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function ProductCard({ p }) {
  return (
    <Link to={`/retailer/product/${p.id}`} data-testid={`product-card-${p.id}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-transform duration-200 hover:-translate-y-0.5">
      <div className="aspect-square overflow-hidden bg-psage/40">
        {p.images?.[0]
          ? <img src={p.images[0]} alt={p.name} className="h-full w-full object-cover" />
          : <div className="flex h-full items-center justify-center text-msage font-heading text-3xl">{p.brand?.[0] || "S"}</div>}
      </div>
      <div className="flex flex-1 flex-col p-3">
        <span className="overline text-olive">{p.brand}</span>
        <p className="mt-0.5 line-clamp-2 text-sm font-medium text-ink">{p.name}</p>
        <div className="mt-auto pt-2">
          <div className="flex items-baseline gap-2">
            <Money value={p.retailer_price} className="text-base font-semibold text-forest" />
            <Money value={p.mrp} strike className="text-xs" />
          </div>
          <span className="mt-1 inline-block rounded-full bg-psage px-2 py-0.5 text-xs font-semibold text-forest num">
            {p.margin_pct}% margin
          </span>
        </div>
      </div>
    </Link>
  );
}
